import type Highcharts from "highcharts";
import { DateTime } from "luxon";

export type Resolution = "as_measured" | "hourly" | "daily";

type TooltipSeriesOptions = {
  tooltip?: { valueSuffix?: string };
};

type ChartTooltipOptions = {
  resolution: Resolution;
  dateFormats?: Record<Resolution, string>;
};

const DEFAULT_DATE_FORMATS: Record<Resolution, string> = {
  as_measured: "yyyy-MM-dd HH:mm:ss",
  hourly: "yyyy-MM-dd HH:mm",
  daily: "yyyy-MM-dd",
};

/**
 * Formats a single point for tooltip display
 */
const formatPoint = (
  name: string,
  color: string | Highcharts.GradientColorObject | Highcharts.PatternObject,
  value: number,
  suffix: string
): string => {
  const colorStr = typeof color === "string" ? color : String(color || "#000");
  return `<span style="color:${colorStr}">\u25CF</span> ${name}: <b>${value.toFixed(1)}${suffix}</b><br/>`;
};

/**
 * Creates a Highcharts tooltip formatter configuration for time series charts
 *
 * @param options - Configuration options
 * @param options.resolution - Chart resolution (affects date formatting)
 * @param options.dateFormats - Custom date format overrides
 *
 * @returns Highcharts tooltip configuration object
 */
export const createChartTooltip = ({
  resolution,
  dateFormats = DEFAULT_DATE_FORMATS,
}: ChartTooltipOptions): Highcharts.TooltipOptions => ({
  shared: true,
  split: false,
  valueDecimals: 1,
  formatter: function () {
    const context = this as any;
    const hoveredTimestamp = context.x as number;

    if (hoveredTimestamp === null || hoveredTimestamp === undefined) {
      return "";
    }

    const dateFormat = dateFormats[resolution];
    const date = DateTime.fromMillis(hoveredTimestamp).toFormat(dateFormat);
    let html = `<b>${date}</b><br/>`;
    let hasValidPoints = false;

    const points = context.points;
    if (points && points.length > 0) {
      points.forEach((point: Highcharts.TooltipFormatterContextObject) => {
        if (point.y !== null && point.y !== undefined) {
          hasValidPoints = true;
          const seriesUserOptions = (point.series as any)?.userOptions as
            | TooltipSeriesOptions
            | undefined;
          const seriesOptions = point.series.options as TooltipSeriesOptions;
          const valueSuffix =
            seriesUserOptions?.tooltip?.valueSuffix || seriesOptions?.tooltip?.valueSuffix || "";
          html += formatPoint(point.series.name, point.series.color, point.y, valueSuffix);
        }
      });
    }

    return hasValidPoints ? html : "";
  },
});
